import { routes } from "@/routes/utils";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { dependencyType, ZoomLevel, zoomLevels } from "./consts";
import { toast } from "@/components";
import { useQueryClient } from "@tanstack/react-query";
import { useGetPlanById, useGetTaskCategoriesByProject, useGetTaskList, useGetTaskSubCategories, useGetDependenciesByProjectId, useDeleteTaskMutation } from "@/services";
import { setTaskData } from "@/redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const usePreviewPlan = () => {
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [currTask, setCurrTask] = useState<string>("all");
  const [openTaskDrawer, setOpenTaskDrawer] = useState<boolean>(false);
  const [zoom, setZoom] = useState<ZoomLevel>("day");
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [showEditDelete, setShowEditDelete] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [showView, setShowView] = useState<boolean>(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { id, action, planId, planAction } = useParams();
  const queryClient = useQueryClient();
  const isProjectView = action == "view";
  const isPlanView = planAction == "view";
  const isPlanEdit = planAction == "edit";
  const isAdd = !planId;
  const isView = isProjectView || isPlanView;

  const [orgChartData, setOrgChartData] = useState<any>([]);

  const [chartData, setChartData] = useState<any>({
    tasks: [],
    links: [],
  });

  const {taskData} = useSelector((state: RootState) => state.currentData)


  const {data: _planData, isLoading: isLoadingPlanData } = useGetPlanById({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  })

  const {data: taskList, isLoading: isLoadingTaskList } = useGetTaskList({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  })

  const {data: dependenciesList, isLoading: isLoadingDependencies } = useGetDependenciesByProjectId({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  })

  const {data: categoryList, isLoading: isLoadingCategory } = useGetTaskCategoriesByProject({
    projectId: id as string,
  });

  const {data: subCategoryList, isLoading: isLoadingSubCategory } = useGetTaskSubCategories()

  const timelineRange = useMemo(() => {
    const dates: Date[] = [];
  
    chartData?.tasks?.forEach((t: any) => {
      if (t.start) dates.push(new Date(t.start));
      if (t.end) dates.push(new Date(t.end));
      if (t.planStart) dates.push(new Date(t.planStart));
      if (t.planEnd) dates.push(new Date(t.planEnd));
    });
  
    if (!dates.length) return {};
  
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
  
    min.setDate(min.getDate() - 3);  // left buffer
    max.setDate(max.getDate() + 3);  // right buffer
  
    return { start: min, end: max };
  }, [chartData]);
  
  const transformToGanttData = (apiTasks: any[]) => {
    const tasks: any[] = [];
    let links: any[] = [];
  
    let ganttId = 1;
  
    const categoryMap = new Map<number, number>();
    const subCategoryMap = new Map<string, number>();
  
    [...apiTasks]?.sort(
      (a: any, b: any) =>
        new Date(a.updatedAt).getTime() -
        new Date(b.updatedAt).getTime(),
    )?.forEach((item) => {
      /* ---------------- CATEGORY ---------------- */
      if (!categoryMap.has(item.categoryId)) {
        categoryMap.set(item.categoryId, ganttId);
  
        tasks.push({
          id: ganttId,
          text: categoryList?.data?.find((val: any) => val.taskCategoryId == item.categoryId)?.title || '',
          parent: 0,
          type: "summary",
          open: true,
        });
  
        ganttId++;
      }
  
      const categoryGanttId = categoryMap.get(item.categoryId)!;
  
      /* ---------------- SUB CATEGORY ---------------- */
      const subKey = `${item.categoryId}-${item.subCategoryId}`;
  
      if (!subCategoryMap.has(subKey)) {
        subCategoryMap.set(subKey, ganttId);
  
        tasks.push({
          id: ganttId,
          text: subCategoryList?.data?.find((val: any) => val.taskSubCategoryId == item.subCategoryId)?.title,
          parent: categoryGanttId,
          type: "summary",
          open: true,
        });
  
        ganttId++;
      }
  
      const subCategoryGanttId = subCategoryMap.get(subKey)!;
   
      // Planned Tasks ----------------------------------------
      tasks.push({
        ...item,
        id: ganttId,
        text: item?.taskName || "",
        parent: subCategoryGanttId,
        type: "task",
        start: item?.plannedStartDate ? new Date(item?.plannedStartDate) : "",
        end: item?.plannedEndDate ? new Date(item?.plannedEndDate) : "",
        progress: 0,
      });
      // ----------------------------------------------------------------------------
      
      ganttId++;

      // Actual Task ---------------------------------------------------------------
      tasks.push({
        id: ganttId,
        text: item?.taskName || "",
        parent: subCategoryGanttId,
        type: "urgent",
        start: item?.actualStartDate ? new Date(item?.actualStartDate) : "",
        end: item?.actualEndDate ? new Date(item?.actualEndDate) :  item?.actualStartDate ? new Date() : "",
        actualEndDate: item?.actualEndDate ? new Date(item?.actualEndDate) :  "",
        progress: item?.actualEndDate ? Number(item?.completionPercentage) : 100,
        actualProgress: Number(item?.completionPercentage),
        isActual: true
      });
      // ----------------------------------------------------------------------------
  

      ganttId++;
    });
  
    tasks.push({
      id: " ",
      text: "",
      parent: "",
      type: "task",
      start: "",
      end: "",
      progress: 0,
    });

    links = dependenciesList?.data?.map((data: any, index: number) => {
      if (data?.dependencyType == "FINISH_TO_START") {
        return ({
          id: index + 1,
          source: (tasks as any).find((val: any) => val.taskId == data?.taskId)?.id,
          sourceName: (tasks as any).find((val: any) => val.taskId == data?.taskId)?.taskName,
          target: (tasks as any).find((val: any) => val.taskId == data?.dependsOnTaskId)?.id,
          type: dependencyType[data?.dependencyType],
          lagDays: data?.lagDays || '0'
        })
      }
      return ({
        id: index + 1,
        source: (tasks as any).find((val: any) => val.taskId == data?.dependsOnTaskId)?.id,
        sourceName: (tasks as any).find((val: any) => val.taskId == data?.dependsOnTaskId)?.taskName,
        target: (tasks as any).find((val: any) => val.taskId == data?.taskId)?.id,
        type: dependencyType[data?.dependencyType],
        lagDays: data?.lagDays || '0'
      })
    })

    return { tasks, links };
  };

  useEffect(() => {
    if (orgChartData?.length) return;
    
    if (taskList?.data && categoryList?.data && subCategoryList?.data) {
      setChartData(transformToGanttData(taskList?.data))
      setOrgChartData(taskList?.data)
    }
  }, [taskList, categoryList, subCategoryList]);

  useEffect(() => {
    if (!orgChartData?.length) return;
  
    if (globalFilter) {
      const searchText = globalFilter.toLowerCase();
  
      const filtered = orgChartData.filter((data: any) =>
        data?.taskName?.toLowerCase()?.includes(searchText) ||
        data?.categoryName?.toLowerCase()?.includes(searchText) ||
        data?.subCategoryName?.toLowerCase()?.includes(searchText)
      );
  
      setChartData(transformToGanttData(filtered));
    } else {
      setChartData(transformToGanttData(orgChartData));
    }
  }, [globalFilter, orgChartData]);

  const columns = useMemo(
    () => [
      { id: "id", header: "ID", sort: false, width: 50 },
      { id: "text", header: "Task", width: 150,
        cell: ({ row }: any) => row.text ? row.type != "summary" ? 
          (row.isActual ? 
            <div className="bg-gray-200 p-4">
              <span className="small-font">{`(Actual) `}</span>
              {`${row.text}`}
            </div> : 
          <div>
            <span className="small-font"><i>{`(Planned) `}</i></span>
            {`${String(row.text)}`}
          </div>) : String(row.text) : ""
      },
      {
        id: "start",
        header: "Start",
        cell: ({ row }: any) =>  row.start ? moment(row.start).format("MM-DD-YYYY") : "",
      },
      {
        id: "end",
        header: "End",
        cell: ({ row }: any) => row?.isActual ? row?.actualEndDate ? moment(row.actualEndDate).format("MM-DD-YYYY") : "" : row?.end ? moment(row.end).format("MM-DD-YYYY") : "",
      },
      { id: "duration", header: "Duration", width: 100,
        cell: ({row}: any) => {
          if (row.isActual) {
            if (row.start && row.actualEndDate) {
              return setDuration(row.start, row.actualEndDate)
            } else {
              return "";
            }
          }
          if (row.start && row.end) {
            return setDuration(row.start, row.end)
          }
          return ""
        }
      },
      { id: "dependency", header: "Dependency", width: 100,
        cell: ({row}: any) => {
          const dep = chartData?.links?.find((deps: any) => deps?.target == row?.id);
          return <span>{dep?.sourceName || ''}</span>
        }
      },
      { id: "progress", header: "% completion", width: 100,
        cell: ({row}: any) => {
          if (row.isActual) {
            if (row?.actualEndDate) {
              return row?.progress || ""
            }
            return row?.actualProgress || ""
          }
          return row?.progress || ""
        } 
      },
    ],
    [chartData]
  );

  const setDuration = (startDate: any, endDate: any) => {
    if (startDate && endDate) {
       return moment(endDate).diff(moment(startDate), "days").toString()
    }
  };

  const onChangeTask = (e: any) => {
    setCurrTask(e);
    if (e == 'all') {
      setChartData(transformToGanttData(orgChartData));
    } else {
      setChartData(transformToGanttData(orgChartData?.filter((data: any) => e?.toLowerCase() == data.actualStatus?.toLowerCase())));
    }
  };

  const onToday = (checked: boolean) => {
    if (checked) {
      setChartData(
        transformToGanttData(
          orgChartData?.filter((data: any) => {
            if (!data?.plannedStartDate || !data?.plannedEndDate) return false;
      
            return moment().isBetween(
              moment(data.plannedStartDate),
              moment(data.plannedEndDate),
              'day',
              '[]'
            );
          })
        )
      );      
    } else {
      setChartData(transformToGanttData(orgChartData));
    }
  }

  const onCancel = () => {
    navigate(
      `/${routes.pm}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`
    );
  };

  const onExpandAll = () => {
    setChartData((prev: any) => ({
      ...prev,
      tasks: prev.tasks.map((t: any) =>
        t.type === "summary" ? { ...t, open: true } : t
      ),
    }));
  };

  const onCollapseAll = () => {
    setChartData((prev: any) => ({
      ...prev,
      tasks: prev.tasks.map((t: any) =>
        t.type === "summary" ? { ...t, open: false } : t
      ),
    }));
  };

  const zoomIn = () => {
    setZoom((prev) => {
      const idx = zoomLevels.indexOf(prev);
      return zoomLevels[Math.max(0, idx - 1)];
    });
  };

  const zoomOut = () => {
    setZoom((prev) => {
      const idx = zoomLevels.indexOf(prev);
      return zoomLevels[Math.min(zoomLevels.length - 1, idx + 1)];
    });
  };

  const { mutate: deleteTask, isPending: isDeletePending } = useDeleteTaskMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["task-list", planId],
      });
      toast({ message: "Task Deleted Successfully" });
    },
  });

  const onDeleteClick = () => {
    setShowDeleteConfirmation(true)
  }

  const onDeleteTask = () => {
    deleteTask({
      planId: planId as string,
      projectId: id as string,
      id: chartData?.tasks?.find((data: any) => data?.id == selectedTask)?.taskId
    })
  };

  const onSelectTask = (ev: any) => {
    setSelectedTask(ev.id);
  }

  useEffect(() => {
    if (selectedTask) {
      if (isView) {
        setShowView(!!chartData?.tasks?.find((data: any) => data.id == selectedTask && data.type == "task"))
        setShowEditDelete(false);
      } else {
        setShowView(false);
        setShowEditDelete(!!chartData?.tasks?.find((data: any) => data.id == selectedTask && data.type == "task"))
      }
    }
  }, [selectedTask]);

  const onEditClick = () => {
    dispatch(setTaskData(chartData?.tasks?.find((data: any) => data.id == selectedTask)))
    setTimeout(() => setOpenTaskDrawer(true), 100)
  }

  const isTaskExist = useMemo(() => {
    return taskList?.data?.length;
  }, [taskList]);

  return {
    chartData,
    globalFilter,
    setGlobalFilter,
    onChangeTask,
    currTask,
    openTaskDrawer,
    setOpenTaskDrawer,
    columns,
    onCancel,
    onExpandAll,
    onCollapseAll,
    zoomIn,
    zoomOut,
    zoom,
    isProjectView,
    isDeletePending,
    isAdd,
    isLoadingPlanData,
    isPlanView,
    isPlanEdit,
    isLoadingTaskList,
    isLoadingCategory,
    isLoadingSubCategory,
    onSelectTask,
    onEditClick,
    showEditDelete,
    onToday,
    isLoadingDependencies,
    isView,
    planAction,
    onDeleteTask,
    showView,
    timelineRange,
    onDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    isTaskExist,
    taskList: taskList?.data || [],
    taskData
  };
};

export default usePreviewPlan;
